import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Paperclip, MessageCircle } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Button } from "@/components/Button";
import { useSiteContent } from "@/context/ContentContext";
import { uploadToImgBB, isImageFile } from "@/lib/imgbb";

const fields = [
  { name: "full_name", label: "Full Name", required: true, col: 1 },
  { name: "company", label: "Company", col: 1 },
  { name: "email", label: "Email", type: "email", required: true, col: 1 },
  { name: "phone", label: "Phone", col: 1 },
  { name: "country", label: "Country", col: 1 },
  { name: "machine_make", label: "Machine Make", col: 1 },
  { name: "machine_model", label: "Machine Model", col: 1 },
  { name: "serial_number", label: "Serial Number", col: 1 },
  { name: "part_number", label: "Part Number", col: 1 },
  { name: "quantity", label: "Quantity", col: 1 },
  { name: "required_date", label: "Required Date", type: "date", col: 1 },
];

const empty = fields.reduce((a, f) => ({ ...a, [f.name]: "" }), { message: "" });

export default function RequestQuote() {
  const { contact } = useSiteContent();
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | success
  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState(null);
  const [via, setVia] = useState(""); // "" | "email" | "whatsapp"
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoWarning, setPhotoWarning] = useState("");

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const onFileChange = (ev) => {
    const f = ev.target.files?.[0] || null;
    setFile(f);
    setFileName(f ? f.name : "");
  };

  const enquiryBody = (photoUrl) => {
    const rows = [
      ["Full Name", form.full_name],
      ["Company", form.company],
      ["Email", form.email],
      ["Phone", form.phone],
      ["Country", form.country],
      ["Machine Make", form.machine_make],
      ["Machine Model", form.machine_model],
      ["Serial Number", form.serial_number],
      ["Part Number", form.part_number],
      ["Quantity", form.quantity],
      ["Required Date", form.required_date],
      ["Message", form.message],
    ].filter(([, v]) => v && String(v).trim());
    if (photoUrl) rows.push(["Photo", photoUrl]);
    return rows.map(([k, v]) => `${k}: ${v}`).join("\n");
  };

  const buildMailto = (photoUrl) => {
    const subject = `Parts Enquiry — ${form.full_name}${form.part_number ? ` (Part ${form.part_number})` : ""}`;
    return `mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(enquiryBody(photoUrl))}`;
  };

  // Only JPEG/PNG/WEBP/GIF can be auto-hosted; PDFs/spreadsheets are skipped here
  // (customer attaches those manually in their email app instead).
  const getPhotoUrl = async () => {
    if (!file || !isImageFile(file)) return null;
    setUploadingPhoto(true);
    setPhotoWarning("");
    try {
      return await uploadToImgBB(file);
    } catch (e) {
      setPhotoWarning("Your photo could not be uploaded automatically — please attach it manually before sending.");
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const onWhatsApp = async () => {
    if (!validate()) return;
    const photoUrl = await getPhotoUrl();
    const text = `Parts Enquiry — ${form.full_name}\n\n${enquiryBody(photoUrl)}`;
    window.open(`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
    setVia("whatsapp");
    setStatus("success");
    setForm(empty);
    setFile(null);
    setFileName("");
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    const photoUrl = await getPhotoUrl();
    window.location.href = buildMailto(photoUrl);
    setVia("email");
    setStatus("success");
    setForm(empty);
    setFile(null);
    setFileName("");
  };

  if (status === "success") {
    return (
      <>
        <PageHero kicker="Request a Quote" title="Enquiry Received" />
        <div className="mx-auto max-w-2xl px-4 sm:px-6 py-24 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }}>
            <CheckCircle2 className="mx-auto w-16 h-16 text-amber" />
          </motion.div>
          <h2 className="mt-6 font-display uppercase text-3xl tracking-wide text-ink" data-testid="quote-success">{via === "whatsapp" ? "Your WhatsApp message is ready" : "Your email is ready to send"}</h2>
          <p className="mt-4 text-muted">{via === "whatsapp" ? "WhatsApp should have opened with your enquiry pre-filled, including your photo link if you attached one — just review and press Send." : "Your email app should have opened with your enquiry pre-filled, including your photo link if you attached one — just review and press Send."}</p>
          {photoWarning && <p className="mt-3 text-sm text-amber-hover" data-testid="quote-photo-warning">{photoWarning}</p>}
          <div className="mt-8 flex justify-center gap-4">
            <Button onClick={() => { setVia(""); setStatus("idle"); }} variant="primary" data-testid="quote-another-btn">Submit Another Enquiry</Button>
            <Button to="/" variant="outlineDark">Back Home</Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHero kicker="Request a Quote" title="Request a Parts Quote"
        text="Share your part number, machine details or component photo. The more detail you provide, the faster we can help." />

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1.5fr_1fr]">
          <form onSubmit={onSubmit} noValidate data-testid="quote-form">
            <div className="grid gap-5 sm:grid-cols-2">
              {fields.map((f) => (
                <div key={f.name} className={f.col === 2 ? "sm:col-span-2" : ""}>
                  <label htmlFor={f.name} className="mb-1.5 block font-body text-xs font-semibold uppercase tracking-wider text-ink">
                    {f.label}{f.required && <span className="text-amber-hover"> *</span>}
                  </label>
                  <input id={f.name} type={f.type || "text"} value={form[f.name]} onChange={(e) => onChange(f.name, e.target.value)}
                    data-testid={`quote-${f.name}`} aria-invalid={!!errors[f.name]}
                    className={`w-full border bg-white py-3 px-4 text-sm outline-none transition-colors focus:border-ink focus-ring ${errors[f.name] ? "border-red-400" : "border-[#E3E8EF]"}`} />
                  {errors[f.name] && <p className="mt-1 text-xs text-red-600" data-testid={`quote-${f.name}-error`}>{errors[f.name]}</p>}
                </div>
              ))}
              <div className="sm:col-span-2">
                <label htmlFor="message" className="mb-1.5 block font-body text-xs font-semibold uppercase tracking-wider text-ink">Message</label>
                <textarea id="message" rows={4} value={form.message} onChange={(e) => onChange("message", e.target.value)} data-testid="quote-message"
                  className="w-full border border-[#E3E8EF] bg-white py-3 px-4 text-sm outline-none transition-colors focus:border-ink focus-ring" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block font-body text-xs font-semibold uppercase tracking-wider text-ink">Attachment</label>
                <label className="flex cursor-pointer items-center gap-3 border border-dashed border-[#CBD3DD] bg-canvas p-4 text-sm text-muted transition-colors hover:border-ink" data-testid="quote-attachment-label">
                  <Paperclip className="w-4 h-4" />
                  <span className="truncate">{fileName || "Choose a component photo (JPEG/PNG/WEBP)"}</span>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.xls,.xlsx" className="hidden" onChange={onFileChange} data-testid="quote-attachment" />
                </label>
                <p className="mt-2 text-xs text-muted" data-testid="quote-attachment-note">
                  A photo (JPEG/PNG/WEBP) is automatically uploaded and a viewable link is included in your enquiry. PDF/Excel files can't be auto-linked — please attach those manually in your email app.
                </p>
              </div>
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button as="button" type="submit" variant="accent" arrow={!uploadingPhoto} disabled={uploadingPhoto} className="w-full sm:w-auto" data-testid="quote-submit">
                {uploadingPhoto ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading photo…</> : "Send via Email"}
              </Button>
              <Button as="button" type="button" onClick={onWhatsApp} disabled={uploadingPhoto} className="w-full sm:w-auto !bg-[#25D366] !text-white hover:!bg-[#1ebe5b]" data-testid="quote-whatsapp-submit">
                {uploadingPhoto ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading photo…</> : <><MessageCircle className="w-4 h-4" /> Send via WhatsApp</>}
              </Button>
            </div>
          </form>

          <aside className="h-fit border border-[#E3E8EF] bg-canvas p-8">
            <h3 className="font-display uppercase text-xl tracking-wide text-ink">Prefer to talk?</h3>
            <p className="mt-3 text-sm text-muted">Reach our team directly with your enquiry.</p>
            <div className="mt-6 space-y-3 text-sm">
              <a href={`tel:${contact.phone}`} className="block border border-[#E3E8EF] bg-white p-3 font-semibold text-ink hover:border-ink transition-colors" data-testid="quote-aside-call">Call · {contact.phoneDisplay}</a>
              <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noopener noreferrer" className="block border border-[#E3E8EF] bg-white p-3 font-semibold text-ink hover:border-ink transition-colors" data-testid="quote-aside-whatsapp">WhatsApp Us</a>
              <a href={`mailto:${contact.email}`} className="block border border-[#E3E8EF] bg-white p-3 font-semibold text-ink hover:border-ink transition-colors break-words" data-testid="quote-aside-email">{contact.email}</a>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
